import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Table, Tag, Dropdown, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  IconArrowRight,
  IconSearch,
  IconStarFilled,
} from "@tabler/icons-react";
import PageHeader from "../../components/header/PageHeader";
import { useToast } from "../../components/toast";
import ConfirmModal from "../../components/modal/ConfirmModal";
import InsuranceFormDrawer from "./InsuranceFormDrawer";
import api from "../../api";
import { formatDateTime } from "../../utils/helper";

export default function Insurance() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState(undefined);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [page, setPage] = useState(1);
  const toast = useToast();

  useEffect(() => {
    api
      .categories_list({ limit: 100, productType: "INSURANCE" })
      .then((res) => {
        if (res?.status === 200) {
          setCategoryOptions(
            res.data.data.map((c) => ({ value: c.id, label: c.label })),
          );
        }
      });
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const res = await api.insurances_list({
      page,
      limit: 10,
      ...(q && { q }),
      ...(categoryId && { categoryId }),
    });
    if (res?.status === 200) {
      setRows(res.data.data);
      setMeta(res.data.meta);
    } else {
      toast("Өгөгдөл авахад алдаа гарлаа", "error");
    }
    setLoading(false);
  }, [page, q, categoryId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setEditingRow(null);
    setDrawerOpen(true);
  };
  const openEdit = (record) => {
    setEditingRow(record);
    setDrawerOpen(true);
  };

  const onDrawerSuccess = (data, isEdit) => {
    toast(isEdit ? "Амжилттай засагдлаа" : "Амжилттай нэмэгдлээ", "success");
    fetchRows();
  };

  const onDelete = async () => {
    setDeleting(true);
    const res = await api.insurances_delete(deleteModal.id);
    if (res?.status === 200) {
      toast("Устгагдлаа", "info");
      setRows((prev) => prev.filter((r) => r.id !== deleteModal.id));
    } else {
      toast(res?.data?.message || "Устгахад алдаа гарлаа", "error");
    }
    setDeleting(false);
    setDeleteModal({ open: false, id: null });
  };

  const columns = [
    {
      title: "Онцлох",
      dataIndex: "featured",
      width: 70,
      align: "center",
      render: (v) =>
        v ? (
          <IconStarFilled size={16} style={{ color: "#faad14" }} />
        ) : (
          <span style={{ color: "#ddd" }}>-</span>
        ),
    },
    {
      title: "Зураг",
      dataIndex: "imageUrl",
      width: 60,
      render: (v) =>
        v ? (
          <img
            src={v}
            alt="insurance"
            style={{ width: 40, height: 25, objectFit: "contain" }}
          />
        ) : (
          <span style={{ color: "#ccc", fontSize: 12 }}>—</span>
        ),
    },
    {
      title: "Нэр",
      dataIndex: "name",
      render: (v, r) => (
        <div>
          <div className="fw-semibold">{v}</div>
          {r.highlight && (
            <div style={{ fontSize: 11, color: "#888" }}>{r.highlight}</div>
          )}
        </div>
      ),
    },
    {
      title: "Байгууллага",
      dataIndex: "issuer",
      render: (v) => v?.name || "—",
    },
    {
      title: "Ангилал",
      dataIndex: "categories",
      render: (cats) =>
        cats?.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {cats.map((c) => (
              <Tag key={c.id}>{c.label}</Tag>
            ))}
          </div>
        ) : (
          <span style={{ color: "#ccc" }}>—</span>
        ),
    },
    {
      title: "Даатгалын зүйл",
      dataIndex: "coverageObject",
      render: (v) => v || "—",
    },
    {
      title: "Хугацаа",
      dataIndex: "durationValue",
      render: (v, r) => (v != null ? `${v} ${r.durationUnit || "сар"}` : "—"),
    },
    {
      title: "Үнэлгээ",
      dataIndex: "rating",
      render: (v) => (v != null ? `⭐ ${v}` : "—"),
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      render: (v) => <div>{formatDateTime(v)}</div>,
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
              {
                key: "delete",
                label: (
                  <span style={{ fontSize: 13, color: "#ff4d4f" }}>Устгах</span>
                ),
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openEdit(record);
              if (key === "delete")
                setDeleteModal({ open: true, id: record.id });
            },
          }}
          trigger={["click"]}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <section className="insurance-section">
      <PageHeader
        title="Даатгал"
        sub="Эрсдэлээс хамгаалах боломжууд"
        rSide={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Нэмэх
          </Button>
        }
      />

      <div className="toolbar">
        <Input
          className="search"
          prefix={<IconSearch size={14} color="#bbb" />}
          placeholder="Даатгалын нэр хайх..."
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          allowClear
        />
        <Select
          className="status"
          placeholder="Ангилал"
          allowClear
          value={categoryId}
          onChange={(v) => {
            setCategoryId(v);
            setPage(1);
            setRows([]);
          }}
          options={categoryOptions}
        />
      </div>

      <div className="table-wrap">
        <Table
          rowKey="id"
          loading={{ spinning: loading, size: "small" }}
          columns={columns}
          dataSource={rows}
          pagination={{
            current: meta?.page,
            pageSize: meta?.limit,
            total: meta?.total,
            onChange: (p) => setPage(p),
          }}
        />
      </div>

      <InsuranceFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        editingRow={editingRow}
        onSuccess={onDrawerSuccess}
      />

      <ConfirmModal
        showModal={deleteModal.open}
        setShowModal={(v) => setDeleteModal((prev) => ({ ...prev, open: v }))}
        submitHandler={onDelete}
        loading={deleting}
        title="Итгэлтэй байна уу?"
        description={
          <>
            <span style={{ fontWeight: 600 }}>"Даатгал устгах"</span> үйлдлийг
            буцаах боломжгүйг анхаарна уу.
          </>
        }
        variant="error"
      />
    </section>
  );
}
